import api from 'api';
import { actions } from 'store';
import { useDispatch, useReduxState } from 'hooks';
import { updateAttributeRequiredPasswords } from 'lib/customAttributes';
import { ModalButtonWithForm } from 'components/common';
import { UserAttributesAttributeForm } from '.';

import type { GetUserCustomAttributesRes } from 'shared-types/responses';
import type { UserAttributesAttributeFormState } from './UserAttributesAttributeForm';

interface Props {
  attribute: GetUserCustomAttributesRes['userCustomAttributes'][0];
  isDropdown?: boolean;
  updateData: () => Promise<void>;
  userCustomAttributes: GetUserCustomAttributesRes['userCustomAttributes'];
}

const UserAttributesEdit = ({
  attribute,
  isDropdown,
  updateData,
  userCustomAttributes
}: Props) => {
  const { dispatch } = useDispatch();

  const { messages } = useReduxState();

  const handleSubmit = async (values: UserAttributesAttributeFormState) => {
    const data = {
      appliesTo: values.appliesTo,
      conditions: values.conditions || [],
      description: values.description,
      descriptionTranslations: values.descriptionTranslations,
      excludeFromExcelExport: values.excludeFromExcelExport,
      isEnabled: values.isEnabled,
      isRequired: values.isRequired,
      max: values.max,
      min: values.min,
      permissions: values.permissions,
      showBeforeRegistration: values.showBeforeRegistration,
      title: values.title,
      titleTranslations: values.titleTranslations,
      validation: values.validation
        ? {
            errorMessage: values.validation.errorMessage,
            errorMessageTranslations:
              values.validation.errorMessageTranslations,
            matchList: values.validation.matchList
              ?.split('\n')
              .filter((option) => option.trim())
              .map((option) => option),
            matchListEnableEditing: values.validation.matchListEnableEditing,
            matchRegex: values.validation.matchRegex,
            requiredPasswords:
              values.validation.requiredPasswords?.map((requiredPassword) => ({
                passwordIdentifier: requiredPassword.passwordIdentifier,
                title: requiredPassword.title,
                titleTranslations: requiredPassword.titleTranslations,
                valueMatchRegex: requiredPassword.valueMatchRegex
              })) || [],
            validateFunction: values.validation.validateFunction
          }
        : null
    };

    if (values.validation?.requiredPasswords?.length) {
      await updateAttributeRequiredPasswords(
        values.validation.requiredPasswords,
        attribute.validation?.requiredPasswords
      );
    }

    await api.put(`/admin/user-custom-attributes/${attribute.id}`, data);

    await updateData();

    dispatch(
      actions.addNotice({
        text: messages.attributesEditSuccess,
        type: 'success'
      })
    );
  };

  const initialValues = {
    appliesTo: attribute.appliesTo,
    conditions: attribute.conditions,
    description: attribute.description,
    descriptionTranslations: attribute.descriptionTranslations,
    excludeFromExcelExport: attribute.excludeFromExcelExport,
    isEnabled: attribute.isEnabled,
    isRequired: attribute.isRequired,
    key: attribute.key,
    max: attribute.max || 100,
    min: attribute.min || 0,
    permissions: attribute.permissions,
    showBeforeRegistration: attribute.showBeforeRegistration,
    title: attribute.title,
    titleTranslations: attribute.titleTranslations,
    type: attribute.type,
    validation: {
      errorMessage: attribute.validation?.errorMessage,
      errorMessageTranslations: attribute.validation?.errorMessageTranslations,
      matchList: attribute.validation?.matchList?.join('\n'),
      matchListEnableEditing: Boolean(
        attribute.validation?.matchListEnableEditing
      ),
      matchRegex: attribute.validation?.matchRegex,
      requiredPasswords:
        attribute.validation?.requiredPasswords?.map((requiredPassword) => ({
          passwordIdentifier: requiredPassword.passwordIdentifier,
          title: requiredPassword.title,
          titleTranslations: requiredPassword.titleTranslations,
          valueMatchRegex: requiredPassword.valueMatchRegex
        })) || [],
      validateFunction: attribute.validation?.validateFunction
    }
  };

  return (
    <ModalButtonWithForm
      buttonCategory="default"
      buttonIcon="pen"
      buttonIsDropdown={Boolean(isDropdown)}
      buttonSize="header"
      buttonText={messages.commonEdit}
      contentStyle={{ padding: 0 }}
      formAllowEmptyStrings={true}
      initialValues={initialValues}
      modalActionCategory="success"
      modalActionText={messages.commonSaveChanges}
      modalTitle={messages.attributesEdit}
      modalWidth="wide"
      onSubmit={handleSubmit}
    >
      {() => (
        <UserAttributesAttributeForm
          attribute={attribute}
          userCustomAttributes={userCustomAttributes}
        />
      )}
    </ModalButtonWithForm>
  );
};

export default UserAttributesEdit;
